import { HeldInputLedger } from '@playcastdotio/PeerCore';
import type { ITrialInputBinding, TuningIdentity } from '@playcastdotio/PeerCore';
import { generators } from '@playcastdotio/Messaging';
import type { PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';

export type GuestTrialInputBindingOptions = {
  hostId: () => string;
  forwardRelease: (message: PlaycastMessage<PlaycastMessageTarget>) => boolean;
  neutralizeGamepad: () => boolean;
};

/** Suppresses blackout traffic and requires held keys/buttons to release before a new down. */
export class GuestTrialInputBinding implements ITrialInputBinding {
  private readonly ledger = new HeldInputLedger();
  private readonly blocked = new Set<string>();
  private suspended = false;
  constructor(private readonly options: GuestTrialInputBindingOptions) {}
  allows(_peerId: string, message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    const transitions = this.ledger.transitions(message), blocked = transitions.some(transition => this.blocked.has(transition.id));
    for (const transition of transitions) if (!transition.pressed) this.blocked.delete(transition.id);
    if (blocked) return false;
    if (this.suspended) { for (const transition of transitions) if (transition.pressed) this.blocked.add(transition.id); return false; }
    if (this.blocked.size && ['keyboard', 'mouse'].includes(message.header.target) && message.header.action === 'setState') return false;
    return true;
  }
  observeForwarded(_peerId: string, message: PlaycastMessage<PlaycastMessageTarget>): void { this.ledger.observe(message); }
  suspend(_target: TuningIdentity): void {
    this.suspended = true;
    for (const release of this.ledger.releases()) if (release.kind !== 'gamepad') this.blocked.add(release.id);
  }
  async neutralize(_target: TuningIdentity): Promise<{ releasesForwarded: number; delivery: 'forwarded' | 'unavailable' }> {
    let forwarded = 0, available = this.ledger.safe();
    for (const release of this.ledger.releases()) {
      if (release.kind === 'gamepad') { if (this.options.neutralizeGamepad()) forwarded++; else available = false; continue; }
      const hostId = this.options.hostId();
      const message = release.kind === 'keyboard' ? generators.keyboard.up(hostId, release.detail)
        : release.kind === 'mouse' ? generators.mouse.up(hostId, release.detail)
          : release.kind === 'touch' ? generators.touch.cancel(hostId, { changedTouches: [release.detail] })
            : generators.touch.up(hostId, release.detail);
      if (this.options.forwardRelease(message)) forwarded++; else available = false;
    }
    this.ledger.clear(); return { releasesForwarded: forwarded, delivery: available ? 'forwarded' : 'unavailable' };
  }
  resume(_target: TuningIdentity): void { this.suspended = false; }
  dispose(_target: TuningIdentity): void { this.ledger.clear(); this.blocked.clear(); this.suspended = false; }
}
